import React, { useState } from 'react';
import { Search, Download, Play, FileText, Music } from 'lucide-react';

interface TranscriptEntry {
  timestamp: number;
  text: string;
  speaker?: string;
}

interface TranscriptViewerProps {
  transcript: TranscriptEntry[];
  currentTime: number;
  onSeek: (time: number) => void;
}

export const TranscriptViewer: React.FC<TranscriptViewerProps> = ({
  transcript,
  currentTime,
  onSeek,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [downloadFormat, setDownloadFormat] = useState<'txt' | 'pdf' | 'mp3' | 'wav'>('txt');

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const filteredTranscript = transcript.filter(entry =>
    entry.text.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDownload = () => {
    console.log(`Downloading transcript in ${downloadFormat} format...`);
  };

  const getCurrentEntry = () => {
    return transcript.findIndex(entry => 
      currentTime >= entry.timestamp && 
      (transcript[transcript.indexOf(entry) + 1]?.timestamp > currentTime || !transcript[transcript.indexOf(entry) + 1])
    );
  };

  const currentEntryIndex = getCurrentEntry();

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100">
      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-2xl font-bold text-gray-900">Interactive Transcript</h3>
          <div className="flex items-center space-x-2">
            <select
              value={downloadFormat}
              onChange={(e) => setDownloadFormat(e.target.value as typeof downloadFormat)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="txt">Text (.txt)</option>
              <option value="pdf">PDF (.pdf)</option>
              <option value="mp3">Audio (.mp3)</option>
              <option value="wav">Audio (.wav)</option>
            </select>
            <button
              onClick={handleDownload}
              className="bg-gradient-to-r from-blue-500 to-green-500 text-white px-4 py-2 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Download</span>
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search in transcript..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Transcript Content */}
      <div className="p-6">
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {filteredTranscript.map((entry, index) => {
            const isActive = index === currentEntryIndex;
            const isHighlighted = searchQuery && entry.text.toLowerCase().includes(searchQuery.toLowerCase());
            
            return (
              <div
                key={index}
                className={`p-4 rounded-xl cursor-pointer transition-all duration-300 ${
                  isActive
                    ? 'bg-blue-50 border-2 border-blue-200 shadow-sm'
                    : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                }`}
                onClick={() => onSeek(entry.timestamp)}
              >
                <div className="flex items-start space-x-3">
                  <button
                    className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                      isActive
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                  >
                    <Play className="w-4 h-4 ml-0.5" />
                  </button>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`text-sm font-medium ${
                        isActive ? 'text-blue-600' : 'text-gray-600'
                      }`}>
                        {formatTime(entry.timestamp)}
                      </span>
                      {entry.speaker && (
                        <>
                          <span className="text-gray-400">•</span>
                          <span className="text-sm text-gray-600 font-medium">
                            {entry.speaker}
                          </span>
                        </>
                      )}
                    </div>
                    
                    <p className={`text-gray-800 leading-relaxed ${
                      isHighlighted ? 'bg-yellow-100 p-1 rounded' : ''
                    }`}>
                      {searchQuery && isHighlighted ? (
                        entry.text.split(new RegExp(`(${searchQuery})`, 'gi')).map((part, i) =>
                          part.toLowerCase() === searchQuery.toLowerCase() ? (
                            <mark key={i} className="bg-yellow-200 px-1 rounded">
                              {part}
                            </mark>
                          ) : (
                            part
                          )
                        )
                      ) : (
                        entry.text
                      )}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredTranscript.length === 0 && searchQuery && (
          <div className="text-center py-8">
            <div className="text-gray-400 mb-2">
              <Search className="w-8 h-8 mx-auto" />
            </div>
            <p className="text-gray-600">No results found for "{searchQuery}"</p>
          </div>
        )}
      </div>

      {/* Audio Player */}
      <div className="p-6 border-t border-gray-100 bg-gray-50">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Music className="w-5 h-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Transcript Audio</span>
          </div>
          <div className="flex-1 bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: '0%' }}
            ></div>
          </div>
          <button className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors">
            <Play className="w-4 h-4 text-gray-700 ml-0.5" />
          </button>
        </div>
      </div>
    </div>
  );
};